import {
  Project,
  ProjectDetail,
  ProjectTodoNode,
  Todo,
  TodoStatus,
} from '@/infra/types/domain';
import { REPLACE_TODO } from '@/features/todos/ducks';

// ─── Domain types for outline state ────────────────────────

// A project todo with its outline position. Mirrors the wire ProjectTodoNode
// but inlines the Todo fields — flat is easier to patch optimistically.
// Override sort_order to nullable (Todo has it as optional number; we want
// an explicit "no order yet" marker distinct from "not loaded").
export type ProjectTodo = Omit<Todo, 'sort_order'> & {
  parent_todo_id: string | null;
  sort_order: number | null;
};

export type UiFilter = 'all' | 'ready' | 'blocked' | 'done';

// ─── State ────────────────────────────────────────────────

export interface ProjectsState {
  projectsById: Record<string, Project>;
  projectIds: string[];
  listLoading: boolean;
  detailLoading: boolean;
  error: string | null;

  detail: {
    projectId: string | null;
    todosById: Record<string, ProjectTodo>;
    todoIds: string[];
    uiFilter: UiFilter;
    focusReadyNow: boolean;
  };

  // Bulk read-only store for the Inbox "Projects" sub-tab: every active
  // project's full task tree in one keyed structure. Distinct from `detail`
  // (single project, editable) — the inbox view renders all active projects'
  // hierarchies at once and never reorders them, so it owns its own slice.
  inboxOutlines: {
    loading: boolean;
    projectIds: string[]; // active projects, in server order
    projectsById: Record<string, Project>; // titles etc. for group headers
    byProject: Record<
      string,
      { todosById: Record<string, ProjectTodo>; todoIds: string[] }
    >;
  };
}

const initialDetail: ProjectsState['detail'] = {
  projectId: null,
  todosById: {},
  todoIds: [],
  uiFilter: 'all',
  focusReadyNow: false,
};

const initialInboxOutlines: ProjectsState['inboxOutlines'] = {
  loading: false,
  projectIds: [],
  projectsById: {},
  byProject: {},
};

const initialState: ProjectsState = {
  projectsById: {},
  projectIds: [],
  listLoading: false,
  detailLoading: false,
  error: null,
  detail: initialDetail,
  inboxOutlines: initialInboxOutlines,
};

// ─── Action Types ─────────────────────────────────────────

export const FETCH_PROJECTS = 'projects/FETCH_PROJECTS';
export const SET_PROJECTS = 'projects/SET_PROJECTS';
export const CREATE_PROJECT = 'projects/CREATE_PROJECT';
export const ADD_PROJECT = 'projects/ADD_PROJECT';
export const SET_PROJECT = 'projects/SET_PROJECT';
export const FETCH_PROJECT_DETAIL = 'projects/FETCH_PROJECT_DETAIL';
export const SET_PROJECT_DETAIL = 'projects/SET_PROJECT_DETAIL';
export const UPDATE_PROJECT = 'projects/UPDATE_PROJECT';
export const ARCHIVE_PROJECT = 'projects/ARCHIVE_PROJECT';
export const UNARCHIVE_PROJECT = 'projects/UNARCHIVE_PROJECT';
export const DELETE_PROJECT = 'projects/DELETE_PROJECT';
export const REMOVE_PROJECT = 'projects/REMOVE_PROJECT';
export const CREATE_TASK = 'projects/CREATE_TASK';
export const MOVE_TODO = 'projects/MOVE_TODO';
export const UPDATE_TODO_POSITION = 'projects/UPDATE_TODO_POSITION';
export const REVERT_TODO_POSITION = 'projects/REVERT_TODO_POSITION';
export const REPLACE_PROJECT_TODO_NODE = 'projects/REPLACE_PROJECT_TODO_NODE';
export const SET_TODO_STATUS = 'projects/SET_TODO_STATUS';
export const SET_TODO_STATUS_LOCAL = 'projects/SET_TODO_STATUS_LOCAL';
export const SET_LIST_LOADING = 'projects/SET_LIST_LOADING';
export const SET_DETAIL_LOADING = 'projects/SET_DETAIL_LOADING';
export const SET_ERROR = 'projects/SET_ERROR';
export const FETCH_INBOX_OUTLINES = 'projects/FETCH_INBOX_OUTLINES';
export const SET_INBOX_OUTLINES = 'projects/SET_INBOX_OUTLINES';
export const SET_INBOX_OUTLINES_LOADING = 'projects/SET_INBOX_OUTLINES_LOADING';

// ─── Action Creators ──────────────────────────────────────

export const fetchProjects = () => ({ type: FETCH_PROJECTS } as const);
export const setProjects = (projects: Project[]) =>
  ({ type: SET_PROJECTS, payload: projects } as const);
export const createProjectAction = (title: string, description?: string) =>
  ({ type: CREATE_PROJECT, payload: { title, description } } as const);
export const addProject = (project: Project) =>
  ({ type: ADD_PROJECT, payload: project } as const);
// Pext-style full-delta replace for a single Project: PATCH /projects/{id}
// returns the canonical Project post-mutation. The saga dispatches this so
// the projectsById entry converges on server truth without a list refetch.
// Full replace (not merge) because proto3 omits cleared optionals — a merge
// would leave stale field values (e.g. a cleared description).
export const setProject = (project: Project) =>
  ({ type: SET_PROJECT, payload: project } as const);
export const fetchProjectDetail = (id: string) =>
  ({ type: FETCH_PROJECT_DETAIL, payload: id } as const);
export const setProjectDetail = (detail: ProjectDetail) =>
  ({ type: SET_PROJECT_DETAIL, payload: detail } as const);
export const updateProjectAction = (id: string, updates: Record<string, unknown>) =>
  ({ type: UPDATE_PROJECT, payload: { id, updates } } as const);
export const archiveProjectAction = (id: string) =>
  ({ type: ARCHIVE_PROJECT, payload: id } as const);
export const unarchiveProjectAction = (id: string) =>
  ({ type: UNARCHIVE_PROJECT, payload: id } as const);
export const deleteProjectAction = (id: string) =>
  ({ type: DELETE_PROJECT, payload: id } as const);
export const removeProject = (id: string) =>
  ({ type: REMOVE_PROJECT, payload: id } as const);
export const createTaskAction = (
  projectId: string,
  title: string,
  parentTodoId?: string | null,
) => ({ type: CREATE_TASK, payload: { projectId, title, parentTodoId: parentTodoId ?? null } } as const);
// Optimistic local move: patches todosById so the outline reflects the
// drag immediately. Saga fires UPDATE_TODO_POSITION to sync with server.
export const moveTodo = (payload: {
  todoId: string;
  parentId: string | null;
  sortOrder: number;
}) => ({ type: MOVE_TODO, payload } as const);
export const updateTodoPosition = (payload: {
  projectId: string;
  todoId: string;
  parentId: string | null;
  sortOrder: number;
  // Snapshot of the pre-move parent + sort_order, so the saga can roll
  // back on error without recomputing.
  prevParentId: string | null;
  prevSortOrder: number | null;
}) => ({ type: UPDATE_TODO_POSITION, payload } as const);
export const revertTodoPosition = (payload: {
  todoId: string;
  parentId: string | null;
  sortOrder: number | null;
}) => ({ type: REVERT_TODO_POSITION, payload } as const);
// Pext-style full-delta replace: after the server resolves a position
// update it returns the canonical ProjectTodoNode (with the post-rebalance
// sort_order). The saga dispatches this so the optimistic moveTodo state
// converges on server truth without waiting for a refetch.
export const replaceProjectTodoNode = (node: ProjectTodoNode) =>
  ({ type: REPLACE_PROJECT_TODO_NODE, payload: node } as const);
export const setTodoStatusAction = (payload: {
  todoId: string;
  status: TodoStatus;
  prevStatus: TodoStatus;
}) => ({ type: SET_TODO_STATUS, payload } as const);
export const setTodoStatusLocal = (payload: { todoId: string; status: TodoStatus }) =>
  ({ type: SET_TODO_STATUS_LOCAL, payload } as const);
export const setListLoading = (loading: boolean) =>
  ({ type: SET_LIST_LOADING, payload: loading } as const);
export const setDetailLoading = (loading: boolean) =>
  ({ type: SET_DETAIL_LOADING, payload: loading } as const);
export const setError = (error: string | null) =>
  ({ type: SET_ERROR, payload: error } as const);
export const fetchInboxOutlines = () =>
  ({ type: FETCH_INBOX_OUTLINES } as const);
export const setInboxOutlines = (details: ProjectDetail[]) =>
  ({ type: SET_INBOX_OUTLINES, payload: details } as const);
export const setInboxOutlinesLoading = (loading: boolean) =>
  ({ type: SET_INBOX_OUTLINES_LOADING, payload: loading } as const);

type ProjectsAction =
  | ReturnType<typeof fetchProjects>
  | ReturnType<typeof setProjects>
  | ReturnType<typeof createProjectAction>
  | ReturnType<typeof addProject>
  | ReturnType<typeof setProject>
  | ReturnType<typeof fetchProjectDetail>
  | ReturnType<typeof setProjectDetail>
  | ReturnType<typeof updateProjectAction>
  | ReturnType<typeof archiveProjectAction>
  | ReturnType<typeof unarchiveProjectAction>
  | ReturnType<typeof deleteProjectAction>
  | ReturnType<typeof removeProject>
  | ReturnType<typeof createTaskAction>
  | ReturnType<typeof moveTodo>
  | ReturnType<typeof updateTodoPosition>
  | ReturnType<typeof revertTodoPosition>
  | ReturnType<typeof replaceProjectTodoNode>
  | ReturnType<typeof setTodoStatusAction>
  | ReturnType<typeof setTodoStatusLocal>
  | ReturnType<typeof setListLoading>
  | ReturnType<typeof setDetailLoading>
  | ReturnType<typeof setError>
  | ReturnType<typeof fetchInboxOutlines>
  | ReturnType<typeof setInboxOutlines>
  | ReturnType<typeof setInboxOutlinesLoading>;

// ─── Reducer ──────────────────────────────────────────────

// mergeServerTruthNode folds a server-truth Todo (from the todos REPLACE_TODO
// action) onto an existing outline node. The plain Todo payload has NO
// parent_todo_id and a differently-typed sort_order, so a wholesale replace
// would flatten the tree. We merge only the schedule/status/owner fields a
// move/claim touches, preserving parent_todo_id and sort_order from `existing`
// (§R2). Shared by the inbox and detail patchers so the two never drift.
//  - owner_id: a drag-assign/claim reassigns the task; merging it keeps the
//    inline owner avatar in sync without a refetch.
//  - completed_at is optional on the wire; only overwrite when the payload
//    carries it so a transition that clears it (reopen) propagates too.
function mergeServerTruthNode(existing: ProjectTodo, todo: Todo): ProjectTodo {
  return {
    ...existing,
    due_at: todo.due_at,
    due_date_only: todo.due_date_only,
    status: todo.status,
    owner_id: todo.owner_id,
    ...(Object.prototype.hasOwnProperty.call(todo, 'completed_at')
      ? { completed_at: todo.completed_at }
      : {}),
  };
}

// patchInboxOutlinesFromTodo merges a server-truth Todo into whichever
// inboxOutlines project holds a node with the same id. Routing is by id-scan
// across all projects, not project_id, so a moved task is found regardless of
// grouping.
function patchInboxOutlinesFromTodo(
  state: ProjectsState,
  todo: Todo,
): ProjectsState {
  if (!todo || !todo.id) return state;
  const { byProject } = state.inboxOutlines;
  for (const projectId of Object.keys(byProject)) {
    const bucket = byProject[projectId];
    const existing = bucket.todosById[todo.id];
    if (!existing) continue;
    const merged = mergeServerTruthNode(existing, todo);
    return {
      ...state,
      inboxOutlines: {
        ...state.inboxOutlines,
        byProject: {
          ...byProject,
          [projectId]: {
            ...bucket,
            todosById: { ...bucket.todosById, [todo.id]: merged },
          },
        },
      },
    };
  }
  return state;
}

// patchDetailFromTodo mirrors patchInboxOutlinesFromTodo for the active
// project-detail outline (state.detail.todosById), which the ProjectsPage
// outline and the task-detail modal both read from. A drag-assign/claim or
// schedule-move dispatches the todos REPLACE_TODO action; merging the
// server-truth owner/scheduling fields keeps the inline owner avatar and the
// modal's assignee dropdown live without a full project refetch (parent_todo_id
// and sort_order are preserved from the existing node).
function patchDetailFromTodo(
  state: ProjectsState,
  todo: Todo,
): ProjectsState {
  if (!todo || !todo.id) return state;
  const existing = state.detail.todosById[todo.id];
  if (!existing) return state;
  const merged = mergeServerTruthNode(existing, todo);
  return {
    ...state,
    detail: {
      ...state.detail,
      todosById: { ...state.detail.todosById, [todo.id]: merged },
    },
  };
}

export function projectsReducer(
  state: ProjectsState = initialState,
  // Open action member (mirrors todosReducer / maintenanceReducer) so the
  // reducer stays assignable to RTK's Reducer<S, UnknownAction> when dropped
  // into a configureStore reducer map (tests do this). In production it is an
  // async-injected plugin reducer; either way it must tolerate foreign actions.
  incoming: ProjectsAction | { type: string; payload?: unknown },
): ProjectsState {
  // Cross-slice listen: a schedule-move or claim on a project task dispatches
  // the todos REPLACE_TODO action (a plain server-truth Todo). Patch the
  // matching node in both inboxOutlines and the active detail outline so the
  // Planned badge / status / owner avatar flip live without a refetch. Handled
  // before the typed switch because REPLACE_TODO is not a projects-owned action
  // (its type string isn't in ProjectsAction).
  if (incoming.type === REPLACE_TODO) {
    const todo = (incoming as { payload: Todo }).payload;
    return patchDetailFromTodo(patchInboxOutlinesFromTodo(state, todo), todo);
  }

  // Unrecognized foreign actions fall through to `default: return state`.
  const action = incoming as ProjectsAction;
  switch (action.type) {
    case SET_PROJECTS: {
      const projectsById: Record<string, Project> = {};
      const projectIds: string[] = [];
      for (const p of action.payload) {
        projectsById[p.id] = p;
        projectIds.push(p.id);
      }
      return { ...state, projectsById, projectIds, listLoading: false, error: null };
    }

    case ADD_PROJECT: {
      const p = action.payload;
      return {
        ...state,
        projectsById: { ...state.projectsById, [p.id]: p },
        projectIds: state.projectIds.includes(p.id) ? state.projectIds : [p.id, ...state.projectIds],
      };
    }

    case SET_PROJECT: {
      // Pext-style full-replace: overwrite the projectsById entry outright
      // so cleared proto3 optionals (e.g. description) don't leave stale
      // values. Preserves list order; the project is the same row whether
      // viewed through the list or the detail (SET_PROJECT_DETAIL also
      // writes to projectsById), so this single dispatch covers both.
      const p = action.payload;
      return {
        ...state,
        projectsById: { ...state.projectsById, [p.id]: p },
        projectIds: state.projectIds.includes(p.id) ? state.projectIds : [...state.projectIds, p.id],
      };
    }

    case SET_PROJECT_DETAIL: {
      const { project, nodes } = action.payload;
      const todosById: Record<string, ProjectTodo> = {};
      const todoIds: string[] = [];
      for (const node of nodes) {
        const t = node.todo;
        const parent = node.parent_todo_id && node.parent_todo_id !== '' ? node.parent_todo_id : null;
        todosById[t.id] = {
          ...t,
          parent_todo_id: parent,
          sort_order: typeof node.sort_order === 'number' ? node.sort_order : null,
        };
        todoIds.push(t.id);
      }
      return {
        ...state,
        projectsById: { ...state.projectsById, [project.id]: project },
        detail: {
          ...state.detail,
          projectId: project.id,
          todosById,
          todoIds,
        },
        detailLoading: false,
      };
    }

    case SET_INBOX_OUTLINES: {
      const projectIds: string[] = [];
      const projectsById: Record<string, Project> = {};
      const byProject: ProjectsState['inboxOutlines']['byProject'] = {};
      for (const detail of action.payload) {
        const { project, nodes } = detail;
        projectIds.push(project.id);
        projectsById[project.id] = project;
        const todosById: Record<string, ProjectTodo> = {};
        const todoIds: string[] = [];
        for (const node of nodes) {
          const t = node.todo;
          // Mirror SET_PROJECT_DETAIL: '' parent → null root; sort_order is a
          // number|null marker distinct from "not loaded".
          const parent =
            node.parent_todo_id && node.parent_todo_id !== ''
              ? node.parent_todo_id
              : null;
          todosById[t.id] = {
            ...t,
            parent_todo_id: parent,
            sort_order: typeof node.sort_order === 'number' ? node.sort_order : null,
          };
          todoIds.push(t.id);
        }
        byProject[project.id] = { todosById, todoIds };
      }
      return {
        ...state,
        inboxOutlines: {
          ...state.inboxOutlines,
          loading: false,
          projectIds,
          projectsById,
          byProject,
        },
      };
    }

    case SET_INBOX_OUTLINES_LOADING:
      return {
        ...state,
        inboxOutlines: { ...state.inboxOutlines, loading: action.payload },
      };

    case MOVE_TODO:
    case REVERT_TODO_POSITION: {
      const { todoId, parentId, sortOrder } = action.payload;
      const existing = state.detail.todosById[todoId];
      if (!existing) return state;
      return {
        ...state,
        detail: {
          ...state.detail,
          todosById: {
            ...state.detail.todosById,
            [todoId]: {
              ...existing,
              parent_todo_id: parentId,
              sort_order: sortOrder,
            },
          },
        },
      };
    }

    case REPLACE_PROJECT_TODO_NODE: {
      const node = action.payload;
      const todo = node.todo;
      if (!todo) return state;
      const parent = node.parent_todo_id && node.parent_todo_id !== '' ? node.parent_todo_id : null;
      const sortOrder = typeof node.sort_order === 'number' ? node.sort_order : null;
      // Full-replace (pext-style): proto3 omits cleared optionals, so a
      // merge would leave stale field values. The server node is the
      // source of truth for the outline-position fields AND the Todo
      // payload (touched_at, etc.) — overwrite todosById[id] outright.
      const alreadyPresent = state.detail.todosById[todo.id] !== undefined;
      return {
        ...state,
        detail: {
          ...state.detail,
          todosById: {
            ...state.detail.todosById,
            [todo.id]: {
              ...todo,
              parent_todo_id: parent,
              sort_order: sortOrder,
            },
          },
          todoIds: alreadyPresent ? state.detail.todoIds : [...state.detail.todoIds, todo.id],
        },
      };
    }

    case SET_TODO_STATUS_LOCAL: {
      const { todoId, status } = action.payload;
      const existing = state.detail.todosById[todoId];
      if (!existing || existing.status === status) return state;
      return {
        ...state,
        detail: {
          ...state.detail,
          todosById: {
            ...state.detail.todosById,
            [todoId]: { ...existing, status },
          },
        },
      };
    }

    case REMOVE_PROJECT: {
      const { [action.payload]: _removed, ...rest } = state.projectsById;
      return {
        ...state,
        projectsById: rest,
        projectIds: state.projectIds.filter((id) => id !== action.payload),
        detail: state.detail.projectId === action.payload ? initialDetail : state.detail,
      };
    }

    case SET_LIST_LOADING:
      return { ...state, listLoading: action.payload };

    case SET_DETAIL_LOADING:
      return { ...state, detailLoading: action.payload };

    case SET_ERROR:
      return { ...state, error: action.payload, listLoading: false, detailLoading: false };

    default:
      return state;
  }
}
